/**
 * Whether a list endpoint's answer left rows out — what a server-side search turns into "verfeinern
 * Sie die Suche".
 *
 * The endpoints answer in two shapes. A `Slice` says so outright with `last`. A `Page` is serialised
 * as `{ content, page: { size, number, totalElements, totalPages } }` and carries no `last` at all, so
 * reading `last === false` off it answered "nothing left out" for every search of the project and
 * employee lists, however many rows had been cut off. Its totals are no substitute either: the
 * employee list reports the page's own size as its total on purpose (a real count would scan the query
 * a second time on every keystroke), so a full page of employees claims to be the only one.
 *
 * Hence `last` where the answer has it, and otherwise the rule the pickers already apply to the lists
 * their screens load (see `useServerSearch`): a full page is a truncated one. At worst that asks for a
 * narrower search when the list happened to fit exactly, which costs a glance — the other mistake
 * costs the one site somebody was looking for.
 *
 * @param pageSize the `itemsPerPage` the request asked for; without one, nothing can be called full.
 */
export function hasMoreRows(
  data: { last?: unknown; content?: unknown[] } | null | undefined,
  pageSize: number | null | undefined
): boolean {
  if (typeof data?.last === "boolean") return !data.last
  if (!pageSize) return false
  return (data?.content?.length ?? 0) >= pageSize
}

/**
 * Whether one of the catalogue searches behind the add dialogs (`/project/{kind}/search/{customerId}`,
 * `/customer/{kind}/search/{customerId}`) left rows out.
 *
 * Those answer with a plain list rather than a `Page`: up to a page of packets followed by up to a page
 * of records, both read with the same `page` and `itemsPerPage`. So either half coming back full means
 * the next page may hold more — of that half; the other one simply answers it with nothing.
 */
export function hasMoreOriginRows(rows: { origin?: string }[] | null | undefined, pageSize: number): boolean {
  const list = rows ?? []
  const packets = list.filter(row => row?.origin === "PACKET").length
  return packets >= pageSize || list.length - packets >= pageSize
}

/**
 * The row count a `Page` answer reports, or null where the answer has none worth showing — a `Slice`,
 * or a list whose total is only its own page size (see [hasMoreRows]); the caller decides which lists
 * count.
 */
export function pageTotal(data: { page?: { totalElements?: unknown } } | null | undefined): number | null {
  const total = data?.page?.totalElements
  return typeof total === "number" ? total : null
}

/**
 * Every row of a paged list, read page after page until [hasMoreRows] says there are no more — for a
 * screen that edits the whole list rather than browsing it (a customer's price lists).
 *
 * @param fetchPage the request for one page, asked with [pageSize]
 */
export async function readAllPages<T>(
  fetchPage: (_page: number) => Promise<{ data?: { content?: T[]; last?: unknown } | null } | null>,
  pageSize = 200
): Promise<T[]> {
  const rows: T[] = []
  for (let page = 0; ; page += 1) {
    const res = await fetchPage(page)
    const content = res?.data?.content ?? []
    rows.push(...content)
    if (!content.length || !hasMoreRows(res?.data, pageSize)) return rows
  }
}
